import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCircleCheck,
  faClock,
  faCircleXmark,
  faPaperPlane,
  faBan,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  portalAccessService,
  CUSTOMER_PORTAL_ROLES,
  type CustomerPortalRoleSlug,
  type PortalAccessState,
} from '@/services/portal-access.service';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';

/**
 * B6.14 — Portal Access panel for a single customer contact.
 *
 * Rendered inside the existing contact management modal; it does not introduce a new
 * page, a new layout, or a new visual language. The Customer Portal itself, its login
 * and its dashboard are untouched by this phase.
 *
 * Everything shown here is gated on `customer-contact:portal:manage` by the caller.
 * That gate is a UX courtesy only — the backend PermissionGuard is the control, and
 * it rejects a request whether or not the button was rendered.
 */

const STATE_META: Record<
  PortalAccessState,
  { label: string; icon: typeof faCircleCheck; className: string }
> = {
  NOT_ENABLED: {
    label: 'Not Enabled',
    icon: faCircleXmark,
    className: 'bg-muted text-muted-foreground',
  },
  INVITATION_PENDING: {
    label: 'Invitation Pending',
    icon: faClock,
    className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  },
  ACTIVE: {
    label: 'Active',
    icon: faCircleCheck,
    className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  },
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export function PortalAccessPanel({
  customerId,
  contactId,
  contactName,
  contactEmail,
}: {
  customerId: string;
  contactId: string;
  contactName: string;
  contactEmail: string | null;
}) {
  const qc = useQueryClient();
  const [roleSlug, setRoleSlug] = useState<CustomerPortalRoleSlug>('customer-readonly');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['portal-access', customerId, contactId],
    queryFn: () => portalAccessService.getStatus(customerId, contactId),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['portal-access', customerId, contactId] });
    void qc.invalidateQueries({ queryKey: ['location-contacts', customerId] });
  };

  const enableM = useMutation({
    mutationFn: () => portalAccessService.enable(customerId, contactId, { roleSlug }),
    onSuccess: () => {
      toast.success(`Portal access enabled — invitation sent to ${contactEmail ?? 'the contact'}`);
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to enable portal access')),
  });

  const disableM = useMutation({
    mutationFn: () => portalAccessService.disable(customerId, contactId),
    onSuccess: () => {
      toast.success('Portal access disabled — all sessions revoked');
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to disable portal access')),
  });

  const resendM = useMutation({
    mutationFn: () => portalAccessService.resendInvitation(customerId, contactId),
    onSuccess: () => {
      toast.success('Invitation resent');
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to resend invitation')),
  });

  const busy = enableM.isPending || disableM.isPending || resendM.isPending;

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
        Could not load portal access status.
      </p>
    );
  }

  const meta = STATE_META[data.state];

  return (
    <div className="space-y-4">
      {/* Status */}
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Portal Access
          </div>
          <div className="mt-1 truncate text-sm font-medium text-foreground">{contactName}</div>
        </div>
        <span
          className={`flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${meta.className}`}
        >
          <FontAwesomeIcon icon={meta.icon} className="h-3 w-3" />
          {meta.label}
        </span>
      </div>

      {/* Detail */}
      {data.state !== 'NOT_ENABLED' && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg bg-muted/30 p-3 text-xs">
          <div>
            <dt className="text-muted-foreground">Username</dt>
            <dd className="truncate font-medium text-foreground">{data.username ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Sign-in email</dt>
            <dd className="truncate font-medium text-foreground">{data.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Role</dt>
            <dd className="font-medium text-foreground">
              {CUSTOMER_PORTAL_ROLES.find((r) => r.slug === data.roleSlug)?.label ??
                data.roleSlug ??
                '—'}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Last sign-in</dt>
            <dd className="font-medium text-foreground">{formatDate(data.lastLoginAt)}</dd>
          </div>
          {data.state === 'INVITATION_PENDING' && (
            <div className="col-span-2">
              <dt className="text-muted-foreground">Invitation expires</dt>
              <dd className="font-medium text-foreground">
                {formatDate(data.invitationExpiresAt)}
              </dd>
            </div>
          )}
        </dl>
      )}

      {/* Not enabled → enable */}
      {data.state === 'NOT_ENABLED' && (
        <div className="space-y-3">
          {!data.canInvite && (
            <p className="flex items-start gap-2 rounded-lg bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
              />
              This contact has no email address. Add one before enabling portal access — the
              invitation is delivered by email.
            </p>
          )}

          <div>
            <label
              htmlFor="portal-role"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Portal role
            </label>
            <select
              id="portal-role"
              value={roleSlug}
              onChange={(e) => setRoleSlug(e.target.value as CustomerPortalRoleSlug)}
              disabled={!data.canInvite || busy}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground disabled:opacity-50"
            >
              {CUSTOMER_PORTAL_ROLES.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              {CUSTOMER_PORTAL_ROLES.find((r) => r.slug === roleSlug)?.hint}
            </p>
          </div>

          <p className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
            Portal access is company-wide: this contact will be able to see jobs, equipment and
            reports across <strong className="text-foreground">all</strong> of this customer&apos;s
            locations.
          </p>

          <Button
            type="button"
            onClick={() => enableM.mutate()}
            disabled={!data.canInvite || busy}
            className="w-full"
          >
            {enableM.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faPaperPlane} className="mr-2 h-3.5 w-3.5" />
            )}
            Enable Portal Access
          </Button>
        </div>
      )}

      {/* Enabled → resend / disable */}
      {data.state !== 'NOT_ENABLED' && (
        <div className="flex flex-col gap-2 sm:flex-row">
          {data.state === 'INVITATION_PENDING' && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => resendM.mutate()}
              disabled={busy || !data.canInvite}
              className="flex-1"
            >
              {resendM.isPending ? (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} className="mr-2 h-3.5 w-3.5" />
              )}
              Resend Invitation
            </Button>
          )}
          <Button
            type="button"
            variant="destructive"
            onClick={() => disableM.mutate()}
            disabled={busy}
            className="flex-1"
          >
            {disableM.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faBan} className="mr-2 h-3.5 w-3.5" />
            )}
            Disable Portal Access
          </Button>
        </div>
      )}

      {data.state !== 'NOT_ENABLED' && (
        <p className="text-xs text-muted-foreground">
          Disabling immediately signs this contact out everywhere and cancels any pending
          invitation. Their password is kept, so access can be restored later without a reset.
        </p>
      )}
    </div>
  );
}
